/**
 * Utility functions for working with APP_ID
 */

/**
 * Get current APP_ID from environment variables or config
 * @returns {string} Current APP_ID (aivard, yagel, etc.)
 */
export const getCurrentAppId = (): string => {
  // First try to get from process.env (set by config-overrides.js during build)
  if (process.env.APP_ID) {
    return process.env.APP_ID
  }
}

/**
 * Check if current APP_ID matches the given value
 * @param {string} appId - APP_ID to check against
 * @returns {boolean} True if current APP_ID matches
 */
export const isAppId = (appId: string): boolean => {
  return getCurrentAppId() === appId
}

/**
 * Check if current APP_ID is Aivard
 * @returns {boolean} True if current APP_ID is 'aivard'
 */
export const isAivard = (): boolean => {
  return isAppId('aivard')
}

/**
 * Check if current APP_ID is Yagel
 * @returns {boolean} True if current APP_ID is 'yagel'
 */
export const isYagel = (): boolean => {
  return isAppId('yagel')
}

/**
 * Get app-specific configuration based on current APP_ID
 * @param {Record<string, any>} configs - Object with app-specific configurations
 * @returns {any} Configuration for current APP_ID or default
 */
export const getAppSpecificConfig = <T>(configs: Record<string, T>, defaultConfig?: T): T => {
  const currentAppId = getCurrentAppId()
  return configs[currentAppId] || defaultConfig || configs['aivard']
}

/**
 * Conditional rendering helper based on APP_ID
 * @param {Record<string, React.ReactNode>} components - Object with app-specific components
 * @param {React.ReactNode} defaultComponent - Default component to render
 * @returns {React.ReactNode} Component for current APP_ID or default
 */
export const renderByAppId = (
  components: Record<string, React.ReactNode>,
  defaultComponent?: React.ReactNode
): React.ReactNode => {
  const currentAppId = getCurrentAppId()
  return components[currentAppId] || defaultComponent || components['aivard']
}
